import { UpdateOrganizationModel } from "../../../../../factories/contacts/models/update-organization.model";
import { IOrganizationContact } from "../../../../../types/types";

export type PrimaryContactFormValues = {
  primaryContactName: string;
  primaryContactPhone: string;
  primaryContactEmail: string;
};

export const getNameParts = (fullName?: string) => {
  const parts = (fullName || "").trim().split(/\s+/).filter(Boolean);
  const [firstName = "", ...lastNameParts] = parts;

  return {
    firstName,
    lastName: lastNameParts.join(" "),
  };
};

export const hasPrimaryContactData = (data: PrimaryContactFormValues) =>
  Boolean(
    data.primaryContactName.trim() ||
      data.primaryContactPhone.trim() ||
      data.primaryContactEmail.trim(),
  );

export const getPrimaryContactPayload = (
  data: PrimaryContactFormValues,
): Required<UpdateOrganizationModel["contactPersons"]> => {
  const primaryContact = getNameParts(data.primaryContactName);

  return {
    firstName: primaryContact.firstName,
    lastName: primaryContact.lastName,
    emails: [],
    phoneNumbers: [],
  };
};

export const buildOrganizationUpdatePayload = (
  organization: IOrganizationContact,
  primaryContactData: PrimaryContactFormValues,
  phoneNumberTypeEnumId?: string,
): UpdateOrganizationModel => ({
  id: organization.id,
  contactStatusEnumId: organization.contactStatusEnumId,
  companyName: organization.companyName || "",
  dba: organization.dba || "",
  addresses:
    organization.addresses?.map((address) => ({
      id: address.id,
      line1: address.line1 || "",
      city: address.city || "",
      region: address.region || "",
      postalCode: address.postalCode || "",
      addressTypeEnumId: address.addressTypeEnumId,
    })) || [],
  emails: [
    ...(organization.emails?.map((email) => ({
      emailAddress: email.emailAddress,
      isPrimaryEmail: email.isPrimaryEmail,
    })) || []),
    ...(primaryContactData.primaryContactEmail.trim()
      ? [
          {
            emailAddress: primaryContactData.primaryContactEmail.trim(),
            isPrimaryEmail: true,
          },
        ]
      : []),
  ].filter((email) => email.emailAddress.trim()),
  incorporationDate: organization.incorporationDate
    ? new Date(organization.incorporationDate).toISOString()
    : null,
  socialIdentificationNumber: organization.socialIdentificationNumber || "",
  additionalInfo: organization.additionalInfo || "",
  contactTypeEnumId: organization.contactTypeEnumId,
  organizationTypeId: organization.organizationTypeId || null,
  businessNumber: organization.businessNumber || "",
  phoneNumbers: [
    ...(organization.phoneNumbers?.map((phoneNumber) => ({
      number: phoneNumber.number,
      phoneNumberTypeEnumId: phoneNumber.phoneNumberTypeEnumId,
    })) || []),
    ...(primaryContactData.primaryContactPhone.trim() && phoneNumberTypeEnumId
      ? [
          {
            number: primaryContactData.primaryContactPhone.trim(),
            phoneNumberTypeEnumId,
          },
        ]
      : []),
  ].filter((phoneNumber) => phoneNumber.number.trim()),
  salesTaxId: organization.salesTaxId || null,
  yearEndMonth:
    (organization.yearEndMonth as UpdateOrganizationModel["yearEndMonth"]) ||
    null,
  yearEndDay: organization.yearEndDay || 0,
  gstHstNumber: organization.gstHstNumber || "",
  pstNumber: organization.pstNumber || "",
  gstHstReportingPeroid: organization.gstHstReportingPeroid || "",
  pstReportingPeroid: organization.pstReportingPeroid || "",
  // Names only: lists left out keep the contact person's saved emails/phones
  // (QA batch 17; empty lists deleted them).
  contactPersons: {
    firstName: getPrimaryContactPayload(primaryContactData).firstName,
    lastName: getPrimaryContactPayload(primaryContactData).lastName,
  },
});
